// Must run before the Supabase client is imported: captures the URL hash while
// it still holds any password-recovery token/error, before the client strips it.
import './utils/authRecoveryHash';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider } from '@chakra-ui/react';
import { Analytics } from '@vercel/analytics/react';
import { PostHogProvider } from '@posthog/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { registerSW } from 'virtual:pwa-register';
import theme from './theme/theme';
import { initAnalytics, posthog } from './lib/posthog';

// Start PostHog before the first render so the initial pageview is captured
// with the landing URL (and its campaign parameters) intact. No-ops when
// VITE_POSTHOG_PROJECT_TOKEN is unset. @vercel/analytics stays alongside it:
// it answers "how much traffic / how fast", PostHog answers "who did what".
initAnalytics();

const container = document.getElementById('root');

const app = (
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <ChakraProvider theme={theme}>
        <App />
        <Analytics />
      </ChakraProvider>
    </PostHogProvider>
  </React.StrictMode>
);

// When the current path was prerendered at build time (see
// scripts/prerender-seo.mjs), #root already holds its markup and is stamped
// with the path it was rendered for — hydrate onto it so the server paint
// survives and nothing is thrown away. Otherwise — an app/auth route, or an
// unmatched path that the SPA fallback served with ANOTHER route's prerendered
// markup — start fresh: clear any stale markup so it can't flash, then
// client-render. Matching on the exact path prevents hydrating Login onto the
// Landing markup that the /index.html fallback carries.
const prerenderedPath = container.dataset.prerenderedPath;
if (prerenderedPath && prerenderedPath === window.location.pathname) {
  ReactDOM.hydrateRoot(container, app);
} else {
  if (container.hasChildNodes()) container.innerHTML = '';
  ReactDOM.createRoot(container).render(app);
}

registerSW({ immediate: true });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
